import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const EMPTY_ITEM = { name: "", quantity: 0, unit: "pcs", reorder_level: 0, category: "general" };

export function ItemFormDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState(EMPTY_ITEM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(editing ? { ...editing } : EMPTY_ITEM);
  }, [open, editing]);

  const saveItem = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, quantity: parseFloat(form.quantity), reorder_level: parseFloat(form.reorder_level) };
      if (editing) await api.put(`/accounting/inventory/${editing.id}`, payload);
      else await api.post("/accounting/inventory", payload);
      toast.success(editing ? "Item updated" : "Item added");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="item-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{editing ? `Edit ${editing.name}` : "Add store item"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={saveItem} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Item name</Label>
            <Input data-testid="item-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Bed linen sets" required />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Quantity</Label>
              <Input type="number" min="0" step="0.5" data-testid="item-form-quantity" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label>Unit</Label>
              <Input data-testid="item-form-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label>Reorder level</Label>
              <Input type="number" min="0" step="0.5" data-testid="item-form-reorder" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} required />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Category</Label>
            <Input data-testid="item-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. housekeeping, kitchen" />
          </div>
          <Button type="submit" disabled={saving} data-testid="item-form-submit" className="w-full transition-colors">
            {saving ? "Saving…" : editing ? "Save changes" : "Add item"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function UsageDialog({ item, onClose, onSaved }) {
  const [mode, setMode] = useState("usage");
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const amount = parseFloat(qty);
      const { data } = await api.post(`/accounting/inventory/${item.id}/usage`, {
        change: mode === "usage" ? -amount : amount, note,
      });
      if (data.low_stock && data.alert) toast.warning(data.alert, { duration: 6000 });
      else toast.success("Stock updated");
      setQty(""); setNote("");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="usage-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Log stock movement — {item?.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <RadioGroup value={mode} onValueChange={setMode} className="flex gap-3" data-testid="usage-mode">
            <label className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-sm border px-3 py-2.5 text-sm font-medium transition-colors ${mode === "usage" ? "border-primary bg-primary/10 text-primary" : "border-border"}`} data-testid="usage-mode-usage">
              <RadioGroupItem value="usage" className="sr-only" /> Usage (−)
            </label>
            <label className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-sm border px-3 py-2.5 text-sm font-medium transition-colors ${mode === "restock" ? "border-primary bg-primary/10 text-primary" : "border-border"}`} data-testid="usage-mode-restock">
              <RadioGroupItem value="restock" className="sr-only" /> Restock (+)
            </label>
          </RadioGroup>
          <div className="space-y-1.5">
            <Label>Quantity ({item?.unit})</Label>
            <Input type="number" min="0.5" step="0.5" data-testid="usage-quantity" value={qty} onChange={(e) => setQty(e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <Label>Note (optional)</Label>
            <Input data-testid="usage-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Room 204 deep clean" />
          </div>
          <Button type="submit" disabled={saving} data-testid="usage-submit" className="w-full transition-colors">
            {saving ? "Saving…" : "Log movement"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
